import React from 'react';
import { QuestionRendererProps } from './QuestionRenderer';
import { MeasurementValue } from '../../../types/survey.types';

const DEFAULT_UNITS = ['mg', 'mL', 'g', 'IU', 'mcg', 'units'];

const MeasurementRenderer: React.FC<Omit<QuestionRendererProps, 'questionNumber'>> = ({
  question,
  value,
  onChange,
  error,
}) => {
  const measurement: MeasurementValue = value && typeof value === 'object'
    ? value as MeasurementValue
    : { amount: '' as any, unit: question.settings?.defaultMeasurementUnit || 'mg' };

  const units = question.settings?.measurementUnits || DEFAULT_UNITS;
  const min = question.settings?.measurementMinValue;
  const max = question.settings?.measurementMaxValue;
  const step = question.settings?.measurementStep;

  const borderClass = error
    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
    : 'border-gray-200 focus:border-blue-500 focus:ring-blue-500/20';

  const inputClass = `px-4 py-2.5 text-base border-2 ${borderClass} rounded-xl bg-white focus:bg-blue-50/50 focus:ring-2 transition-all outline-none`;

  const update = (partial: Partial<MeasurementValue>) => {
    onChange({ ...measurement, ...partial });
  };

  return (
    <div>
      <div className="flex items-center gap-3">
        <input
          type="number"
          value={measurement.amount ?? ''}
          onChange={(e) => {
            const val = e.target.value === '' ? undefined : parseFloat(e.target.value);
            update({ amount: val as any });
          }}
          placeholder={question.placeholder || 'Enter amount'}
          min={min}
          max={max}
          step={step || 'any'}
          className={`flex-1 ${inputClass}`}
          aria-invalid={!!error}
          aria-describedby={error ? `${question.id}-error` : undefined}
        />
        <select
          value={measurement.unit}
          onChange={(e) => update({ unit: e.target.value })}
          className={`w-28 ${inputClass} cursor-pointer`}
        >
          {units.map((unit) => (
            <option key={unit} value={unit}>{unit}</option>
          ))}
        </select>
      </div>
      {measurement.amount !== undefined && measurement.amount !== ('' as any) && (
        <p className="text-sm text-gray-500 mt-1.5 font-medium">
          {measurement.amount} {measurement.unit}
        </p>
      )}
      {(min !== undefined || max !== undefined) && (
        <p className="text-sm text-gray-500 mt-1">
          {min !== undefined && max !== undefined && `Value must be between ${min} and ${max}`}
          {min !== undefined && max === undefined && `Minimum value: ${min}`}
          {min === undefined && max !== undefined && `Maximum value: ${max}`}
        </p>
      )}
    </div>
  );
};

export default MeasurementRenderer;
